import { inject } from '@angular/core';
import { type WritableDraft } from 'immer';
import { type Observable, of } from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import {
  type ImageBehavior as CoreImageBehavior,
  type ElementLink,
} from '@blaze-shared/elements-domain';

import { type EngineState } from '../../../+state/engine';
import {
  type ElementHandler,
  findElement,
  getElement,
  IconResolverService,
  patchTitle,
  upsertElement,
} from '../../../ai';
import { ImageBehavior as AiImageBehavior } from '../../../ai/models/image';
import { toHeightSettings, toWidthSettings } from '../../../ai/models/styles';
import { type ImageClickAction, type ImageEmptyThumbnail } from '../image.models';

import { type ImageContext, type Link } from './image.definition';

export class ImageElementHandler implements ElementHandler<'image'> {
  private readonly iconResolverService = inject(IconResolverService);

  readonly type = 'image';

  prepare(context: ImageContext): Observable<ImageContext> {
    if (context.element.iconName) {
      context.element.iconName = this.iconResolverService.resolveFullIconName(
        context.element.iconName,
      );
    }

    return of(context);
  }

  process(context: ImageContext, draft: WritableDraft<EngineState>): void {
    const props = this.getImageProps(context, draft);
    const result = upsertElement(draft, context, props);
    const element = getElement(draft, result.elementId, 'image');

    if (context.element.width) {
      element.width = toWidthSettings(context.element.width);
    } else {
      element.width = {
        mode: 'full',
      };
    }

    if (context.element.height) {
      element.height = toHeightSettings(context.element.height);
    } else {
      element.height = {
        mode: 'auto',
      };
    }

    if (context.element.comment) {
      // this is most likely temporary
      (element as CanvasElementFrom<'image'> & { comment: string }).comment =
        context.element.comment;
    }
  }

  private getInitialProps(
    context: ImageContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'image'> {
    const element = findElement<'image'>(draft, context.element.id);
    return (
      element?.props ?? {
        content: {
          options: {
            readonly: context.element.readOnly ?? false,
          },
        },
      }
    );
  }

  private getImageProps(
    context: ImageContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'image'> {
    const { element } = context;

    let props = this.getInitialProps(context, draft);

    if (context.element.title) {
      props = patchTitle<'image'>(props, context.element.title, context.element.showTitle);
    }

    if (element.altText) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            altText: element.altText,
          },
        },
      });
    }

    if (element.imageEmptyState?.$type === 'text') {
      props = applyPropsPatch(props, {
        content: {
          options: {
            emptyText: element.imageEmptyState?.$type,
          },
        },
      });
    }

    // if (element.source?.$type === 'workflow' && element.columnName) {
    //   props = applyPropsPatch(props, {
    //     data: [
    //       {
    //         key: 'value',
    //         enabled: true,
    //         bindings: {
    //           value: getWorkflowColumnId(
    //             workflowMapping,
    //             element.source.workflowId,
    //             element.columnName,
    //           ) as ColumnId,
    //         },
    //         isCustomSelection: false,
    //         dataSource: {
    //           id: element.source.workflowId,
    //           sourceType: 'context',
    //           columns: [],
    //         },
    //       },
    //     ],
    //   });
    // }

    // if (element.source?.$type === 'literal') {
    //   props = applyPropsPatch(props, {
    //     content: {
    //       imageSource: {
    //         url: element.source.value,
    //         enabled: true,
    //       },
    //     },
    //   });
    // }

    if (element.imageUrl) {
      props = applyPropsPatch(props, {
        content: {
          imageSource: {
            url: element.imageUrl,
            enabled: true,
          },
        },
      });
    }

    if (element.imageBehavior) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            behavior: this.getImageBehavior(element.imageBehavior),
          },
        },
      });
    } else {
      props = applyPropsPatch(props, {
        content: {
          options: {
            behavior: 'contain',
          },
        },
      });
    }

    if (element.clickAction) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            clickAction: this.getClickAction(element.clickAction),
          },
        },
      });
    }

    if (element.imageEmptyState) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            emptyThumbnail: this.getEmptyThumbnail(element.imageEmptyState),
          },
        },
      });
    }

    if (element.height?.$type === 'fixed') {
      props = applyPropsPatch(props, {
        content: {
          options: {
            forceMaxHeight: `${element.height.height}px`,
          },
        },
      });
    }

    if (element.linksTo) {
      props = applyPropsPatch(props, {
        content: {
          link: this.getLink(element.linksTo),
        },
      });
    }

    return props;
  }

  private getImageBehavior(behavior: AiImageBehavior): CoreImageBehavior {
    return behavior === AiImageBehavior.Cover ? 'cover' : 'contain';
  }

  private getClickAction(action?: 'download' | 'expand'): ImageClickAction | undefined {
    return action;
  }

  private getEmptyThumbnail(
    state: ImageContext['element']['imageEmptyState'],
  ): ImageEmptyThumbnail {
    if (state?.$type === 'icon' && state.iconName) {
      const name = state.iconName.toLowerCase();
      if (name.includes('avatar')) {
        return 'avatar-1';
      }
      return 'image-1';
    }
    return 'custom-text';
  }

  private getLink(link?: Link): ElementLink | undefined {
    if (!link || link.$type === 'none') {
      return undefined;
    }

    if (link.$type === 'url') {
      return {
        navigateUrl: link.url,
      };
    }
    if (link.$type === 'page') {
      return {
        navigatePageId: link.pageName,
      };
    }
    return undefined;
  }
}
